import { useEffect, useMemo } from 'react';
import type { ParsedEdfInfo } from '@lmt/signal-viewer-processor';
import { TherapyTimeInterval } from '@lmt/signal-viewer-ui';
import {
  DAY_MS,
  getStartOfDayMs,
  MIDDAY_OFFSET_MS,
} from '../utils/date-helper';

const addCoveredDaysByMode = (
  availableDaysSet: Set<number>,
  dataStartMs: number,
  dataEndMs: number,
  mode: TherapyTimeInterval,
): void => {
  const shift = mode === TherapyTimeInterval.TherapyDay ? -MIDDAY_OFFSET_MS : 0;

  let dayStart = getStartOfDayMs(dataStartMs + shift);
  const lastDayStart = getStartOfDayMs(dataEndMs - 1 + shift);

  while (dayStart <= lastDayStart) {
    availableDaysSet.add(dayStart);
    dayStart += DAY_MS;
  }
};

const computeAvailableDays = (
  parsedEdfInfo: ParsedEdfInfo[],
  therapyTimeInterval: TherapyTimeInterval,
): number[] => {
  const availableDaysSet = new Set<number>();

  for (const fileData of parsedEdfInfo) {
    const fileStartMs = fileData.startMs;
    const fileEndMs = fileData.startMs + fileData.duration;

    addCoveredDaysByMode(
      availableDaysSet,
      fileStartMs,
      fileEndMs,
      therapyTimeInterval,
    );
  }

  return Array.from(availableDaysSet).sort((a, b) => a - b);
};

type UseAvailableDaysOptions = {
  parsedEdfInfo: ParsedEdfInfo[];
  therapyTimeInterval: TherapyTimeInterval;
  selectedDayMs: number | null;
  onSelectedDayChange: (dayMs: number | null) => void;
};

export const useAvailableDays = ({
  parsedEdfInfo,
  therapyTimeInterval,
  selectedDayMs,
  onSelectedDayChange,
}: UseAvailableDaysOptions): number[] => {
  const availableDays = useMemo(
    () => computeAvailableDays(parsedEdfInfo, therapyTimeInterval),
    [parsedEdfInfo, therapyTimeInterval],
  );

  useEffect(() => {
    if (!availableDays.length) {
      if (selectedDayMs !== null) {
        onSelectedDayChange(null);
      }
      return;
    }

    if (selectedDayMs !== null && availableDays.includes(selectedDayMs)) {
      return;
    }

    onSelectedDayChange(availableDays[0]);
  }, [availableDays, selectedDayMs, onSelectedDayChange]);

  return availableDays;
};
